import { type PluginListenerHandle, Capacitor } from "@capacitor/core";
import {
  type PushNotificationsPlugin,
  PushNotifications,
} from "@capacitor/push-notifications";

export const isNativePush = () => Capacitor.isNativePlatform();

export function assertNativeInstance(apiUrl: string, expected: unknown) {
  if (
    typeof expected !== "string" ||
    !expected ||
    apiUrl.replace(/\/$/, "") !== expected ||
    new URL(apiUrl).protocol !== "https:"
  ) {
    throw new Error(
      "This mobile app requires its configured private HTTPS instance.",
    );
  }
}

type Subscription = { endpoint: string; p256dh: string; auth: string };

// A native installation has one push token; serialize registration and logout.
let pending: Promise<unknown> = Promise.resolve();
export function nativePushOperation<T>(
  operation: () => Promise<T>,
): Promise<T> {
  const result = pending.then(operation);
  pending = result.catch(() => undefined);
  return result;
}

export async function registerNativePush(
  platform: string,
  save: (subscription: Subscription) => Promise<unknown>,
  requestPermission: boolean,
  push: PushNotificationsPlugin = PushNotifications,
) {
  if (platform !== "ios" && platform !== "android")
    throw new Error("Unsupported native platform");
  let permission = await push.checkPermissions();
  if (
    requestPermission &&
    (permission.receive === "prompt" ||
      permission.receive === "prompt-with-rationale")
  ) {
    permission = await push.requestPermissions();
  }
  if (permission.receive !== "granted") return false;
  if (platform === "android") {
    await push.createChannel({
      id: "messages",
      name: "Messages",
      importance: 4,
      visibility: 0,
    });
  }

  const listeners: PluginListenerHandle[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  let resolveToken!: (token: string) => void;
  let rejectToken!: (error: Error) => void;
  const token = new Promise<string>((resolve, reject) => {
    resolveToken = resolve;
    rejectToken = reject;
  });
  try {
    listeners.push(
      await push.addListener("registration", ({ value }) => {
        if (!value || /\s/.test(value) || value.length > 4096) {
          rejectToken(new Error("Invalid native push token"));
        } else {
          resolveToken(value);
        }
      }),
    );
    listeners.push(
      await push.addListener("registrationError", () => {
        rejectToken(new Error("Native push registration failed"));
      }),
    );
    timer = setTimeout(
      () => rejectToken(new Error("Native push registration timed out")),
      15000,
    );
    // Observe token errors immediately, including events emitted during register().
    const [, value] = await Promise.all([push.register(), token]);
    await save({
      endpoint: platform === "ios" ? "apn" : "fcm",
      p256dh: "",
      auth: value,
    });
    return true;
  } finally {
    clearTimeout(timer);
    await Promise.all(listeners.map((listener) => listener.remove()));
  }
}

export async function unregisterNativePush(
  remove: () => Promise<unknown>,
  push: PushNotificationsPlugin = PushNotifications,
) {
  // Keep the session usable if server cleanup fails; never report a false logout.
  try {
    await remove();
  } catch (error) {
    if (
      !error ||
      typeof error !== "object" ||
      !("type" in error) ||
      error.type !== "InvalidSession"
    )
      throw error;
  }
  await push.unregister();
  await push.removeAllDeliveredNotifications();
}

export function nativeNotificationTarget(data: unknown) {
  if (!data || typeof data !== "object") return;
  const payload = data as Record<string, unknown>;
  const message = payload.message;
  const apn =
    message && typeof message === "object"
      ? (message as Record<string, unknown>)
      : undefined;
  const channelId = apn?.channel ?? payload.channel;
  const messageId = apn?._id ?? message;
  const validId = (value: unknown): value is string =>
    typeof value === "string" && /^[0-9A-HJKMNP-TV-Z]{26}$/.test(value);
  if (!validId(channelId)) return;
  return { channelId, messageId: validId(messageId) ? messageId : undefined };
}
